// https://masteringjs.io/tutorials/fundamentals/email-regex
import {SUPERUSER_PERMISSION_ID, USER_ACCESS_PERMISSIONS} from "../constants";
import {validateEmail, validateNonNumeric, validateRequired} from "./index";

const BAD_EMAIL_FORMAT_ERROR = 'Please enter a valid email format. (e.g. simba@priderock.edu)';

const BLANK_FIRST_NAME_ERROR = 'Please enter a first name.';
const BLANK_LAST_NAME_ERROR = 'Please enter a last name.';
const NUMERIC_NAME_ERROR = 'This entry cannot contain any numerical values. Please try again.';

export const DUPLICATE_USER_ERROR = 'is already in use by an existing user. Visit the user profile to edit their account or try a new email.';

export const INCORRECT_CURRENT_PASSWORD_ERROR = 'Your current password was incorrect. Please enter your current password before choosing a new password.'

function validateNameField(value, nameFieldError) {
  const errors = [];

  if (!validateRequired(value)) {
    errors.push(nameFieldError);
  }

  if (!validateNonNumeric(value)) {
    errors.push(NUMERIC_NAME_ERROR);
  }

  return errors.length ? errors : null;
}

function validateEmailField(value) {
  const errors = [];

  if (!validateEmail(value)) {
    errors.push(BAD_EMAIL_FORMAT_ERROR);
  }

  return errors.length ? errors : null;
}

export function validateUserForm(myProfile, validateEmail) {
  const emailErrors = (validateEmail === true) ? validateEmailField(myProfile.email) : null;

  const firstNameErrors = validateNameField(myProfile.firstName, BLANK_FIRST_NAME_ERROR);
  const lastNameErrors = validateNameField(myProfile.lastName, BLANK_LAST_NAME_ERROR);

  if (emailErrors || firstNameErrors || lastNameErrors) {
    return {
      email: emailErrors,
      firstName: firstNameErrors,
      lastName: lastNameErrors
    };
  } else {
    return null;
  }
}

export function userCanAccess(user, permission) {
  let userPermissions = [];

  //This function doesn't require a permission
  if (!permission) {
    return true;
  }

  if (!user) {
    return false;
  }

  const permissionAuthority = user.permission.authority;


  //Super User has access to everything
  if (permissionAuthority === 'ROLE_PERMISSION_SUPER_USER') {
    return true;
  }

  if (USER_ACCESS_PERMISSIONS[permissionAuthority]) {
    userPermissions = USER_ACCESS_PERMISSIONS[permissionAuthority];
  }

  return userPermissions.find((userPermission) => userPermission === permission);
}

export function isUserASuperUser(userProfile) {
  return userProfile.userPermission.permissionId === SUPERUSER_PERMISSION_ID;
}

export function userHasOneDistrict(user) {
  return (!isUserASuperUser(user) && (user.districtList && user.districtList.length === 1));
}

export function filterUserDistricts(user, districts) {
  if (!user) {
    return [];
  }

  return districts;
}

export function userHasSchools(user) {
  return (user.schoolList && user.schoolList.length > 0 && user.schoolList[0].id);
}

export function getUserSchools(user) {
  if (!user) {
    return [];
  }

  switch (user.permission.authority) {
    case 'ROLE_PERMISSION_SUPERUSER':
    case 'ROLE_PERMISSION_ADMIN':
      return [];
    default:
      return user.schoolList;
  }
}

export function userHasSchool(userComposite, schoolId) {
  switch (userComposite.permission.authority) {
    case 'ROLE_PERMISSION_SUPER_USER':
    case 'ROLE_PERMISSION_ADMIN':
      return true;
    case 'ROLE_PERMISSION_BATCH':
      if (userComposite.districtList && userComposite.districtList.length > 0) {
        return true;
      }
      return userComposite.schoolList.find((school) => school.id===schoolId);
    default:
      return false;
  }
}

export function formatUserSchools(userPermission, schools) {
  return schools.map((school) => {
    return {
      schoolId: school.id,
      permissionId: userPermission.permissionId,
      userId: userPermission.userId,
      apiError: "",
      loggedInUserSessionId: ""
    }
  });
}

export function formatUserDistricts(userPermission, districts) {
  return districts.map((district) => {
    return {
      districtId: district.id,
      permissionId: userPermission.permissionId,
      userId: userPermission.userId,
      apiError: "",
      loggedInUserSessionId: ""
    }
  });
}

export function buildPermissionSchools(user, userDistricts) {
  if (user.schoolList && user.schoolList.length > 0 && user.schoolList[0].id) {
    const userSchools = [];
    const userSchoolsObject = {};

    for (const school of user.schoolList) {
      if (!userSchoolsObject[school.districtId]) {
        userSchoolsObject[school.districtId] = [school];
      } else {
        userSchoolsObject[school.districtId].push(school);
      }
    }

    userDistricts.forEach((userDistrict) => {
      userSchools.push(userSchoolsObject[userDistrict.id]);
    });

    return userSchools;
  }

  return [];
}

export function buildPermissionDistricts(user, districts) {
  if (user.schoolList && user.schoolList.length > 0 && user.schoolList[0].id) {
    const userDistricts = [];

    user.schoolList.forEach((school) => {
      if (!userDistricts.find((district) => district.id === school.districtId)) {
        const schoolDistrict = districts.find((district) => district.id === school.districtId);
        userDistricts.push(schoolDistrict);
      }
    });

    return userDistricts;
  } else if (user.districtList && user.districtList.length > 0 && user.districtList[0].id) {
    return user.districtList;
  }

  return [];
}

export function buildPermissionRows(userDistricts) {
  return Array.from(userDistricts.keys());
}

export function userUsesSchools(userComposite) {
  switch (userComposite.permission.authority) {
    case 'ROLE_PERMISSION_SUPER_USER':
    case 'ROLE_PERMISSION_ADMIN':
      return false;
    case 'ROLE_PERMISSION_BATCH':
      return (userComposite.schoolList && userComposite.schoolList.length > 0 && userComposite.schoolList[0].id);
    default:
      return true;
  }
}